import { useState, useEffect } from 'react'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useSalesforce } from '../hooks/useSalesforce'
import { useWorkspace } from '../context/WorkspaceContext'
import Navbar from '../components/Navbar'
import { supabase } from '../services/supabaseClient'
import Chatbot from '../components/Chatbot'
import OrgSwitcher from '../components/OrgSwitcher'
import { FieldCentricTriggerTable } from '../components/triggers/FieldCentricTriggerTable'

// Utility function to clean up object names
const cleanObjectName = (objectName: string): string => {
  return objectName
    .replace(/__c$/, '') // Remove __c suffix
    .replace(/__/g, ' ')  // Replace __ with space
    .replace(/_/g, ' ');  // Replace _ with space
};

interface TriggerPattern {
  triggerFields: string[]
  flowId: string
  flowName: string
  triggerType: string
  performanceGrade: string | null
  performanceScore: number | null
  hasConflict: boolean
  hasNoCriteria: boolean
  matrixDisplay: string
  conditionsDescription: string
}

interface ObjectTriggerGroup {
  objectName: string
  flowCount: number
  triggers: TriggerPattern[]
}

/**
 * Entry Criteria Page that uses pre-calculated trigger data from the database
 */
export default function EntryCriteriaPage() {
  const { signOut } = useAuth()
  const { orgs, isLoading: isLoadingOrgs, userRole } = useSalesforce()
  const { currentOrgId, setCurrentOrgId } = useWorkspace()
  const [searchParams] = useSearchParams()
  const { orgId: urlOrgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [triggerGroups, setTriggerGroups] = useState<ObjectTriggerGroup[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  
  // Filters
  const [selectedObject, setSelectedObject] = useState<string>('all')
  const [selectedTriggerType, setSelectedTriggerType] = useState<string>('all')
  const [selectedTriggerField, setSelectedTriggerField] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState<string>('')

  // Reset trigger field filter when object changes and selected field is no longer available
  useEffect(() => {
    if (selectedTriggerField !== 'all') {
      // Get fields for current object selection
      const availableFields = [...new Set(
        triggerGroups
          .filter(g => selectedObject === 'all' || g.objectName === selectedObject)
          .flatMap(g => g.triggers.flatMap(t => t.triggerFields.filter(f => f)))
      )]

      // If selected field is not in available fields, reset to 'all'
      if (!availableFields.includes(selectedTriggerField)) {
        setSelectedTriggerField('all')
      }
    }
  }, [selectedObject, triggerGroups, selectedTriggerField])

  // Set the selected org from URL params or default to first org
  useEffect(() => {
    // First check URL parameter
    if (urlOrgId) {
      setSelectedOrgId(urlOrgId)
      setCurrentOrgId(urlOrgId)
    } 
    // Fall back to search params for backward compatibility
    else if (searchParams.get('orgId')) {
      const orgId = searchParams.get('orgId')!
      // Redirect to new format
      navigate(`/${orgId}/triggers`, { replace: true })
    } 
    // If no orgId found but orgs exist, redirect to first org
    else if (orgs.length > 0 && !selectedOrgId) {
      navigate(`/${orgs[0].id}/triggers`, { replace: true })
    }
  }, [urlOrgId, searchParams, orgs, selectedOrgId, setCurrentOrgId, navigate])

  // Fetch pre-calculated trigger data from database
  useEffect(() => {
    const fetchTriggerData = async () => {
      if (!selectedOrgId) return

      setIsLoading(true)
      setError(null)

      try {
        // Fetch flows with pre-calculated trigger info
        const { data: flows, error: fetchError } = await supabase
          .from('sf_flow_definitions')
          .select(`
            id,
            master_label,
            developer_name,
            process_type,
            status,
            trigger_info,
            trigger_summary,
            performance_grade,
            performance_score
          `)
          .eq('org_id', selectedOrgId)
          .not('trigger_info', 'is', null)

        if (fetchError) {
          console.error('Error fetching flows:', fetchError)
          throw fetchError
        }

        // Process flows using pre-calculated trigger info
        const processedGroups = processTriggerPatterns(flows || [])
        setTriggerGroups(processedGroups)
      } catch (err: any) {
        console.error('Error fetching trigger data:', err)
        setError(err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchTriggerData()
  }, [selectedOrgId])

  // Process flows using pre-calculated trigger info
  const processTriggerPatterns = (flows: any[]): ObjectTriggerGroup[] => {
    const objectGroups: { [key: string]: TriggerPattern[] } = {}

    flows.forEach(flow => {
      const triggerInfo = flow.trigger_info
      if (!triggerInfo) return
      
      // Skip non-record-triggered flows
      if (!['Before Save', 'After Save', 'Record-Triggered'].includes(triggerInfo.triggerType)) {
        return
      }
      
      // Create trigger pattern from pre-calculated data
      const pattern: TriggerPattern = {
        triggerFields: triggerInfo.triggerFields || [],
        flowId: flow.id,
        flowName: flow.master_label || flow.developer_name,
        triggerType: `${triggerInfo.triggerType} (${triggerInfo.triggerOperations?.join('/') || ''})`,
        performanceGrade: flow.performance_grade,
        performanceScore: flow.performance_score,
        hasConflict: false, // Will be determined later
        hasNoCriteria: triggerInfo.triggerFields?.length === 0 && triggerInfo.entryConditions?.length === 0,
        matrixDisplay: triggerInfo.matrixDisplay || '—',
        conditionsDescription: triggerInfo.conditionsDescription || '—'
      }

      // Add to object group
      const triggerObject = triggerInfo.triggerObject || 'Unknown'
      if (!objectGroups[triggerObject]) {
        objectGroups[triggerObject] = []
      }
      objectGroups[triggerObject].push(pattern)
    })

    // Detect conflicts and create final groups
    const groups: ObjectTriggerGroup[] = []
    
    Object.entries(objectGroups).forEach(([objectName, patterns]) => {
      // Detect conflicts within the object group
      patterns.forEach((pattern, index) => {
        for (let i = index + 1; i < patterns.length; i++) {
          const other = patterns[i]
          if (
            pattern.triggerType === other.triggerType &&
            arraysEqual(pattern.triggerFields, other.triggerFields) &&
            hasOverlappingConditions(pattern, other)
          ) {
            pattern.hasConflict = true
            other.hasConflict = true
          }
        }
      })

      // Sort patterns
      patterns.sort((a, b) => {
        const matrixA = a.matrixDisplay || ''
        const matrixB = b.matrixDisplay || ''
        if (matrixA !== matrixB) return matrixA.localeCompare(matrixB)
        return a.flowName.localeCompare(b.flowName)
      })

      groups.push({
        objectName,
        flowCount: patterns.length,
        triggers: patterns
      })
    })

    // Sort groups by object name
    groups.sort((a, b) => a.objectName.localeCompare(b.objectName))

    return groups
  }

  // Check if arrays are equal
  const arraysEqual = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false
    return a.every((val, index) => val === b[index])
  }

  // Check if conditions overlap (could cause conflicts)
  const hasOverlappingConditions = (a: TriggerPattern, b: TriggerPattern): boolean => {
    // If both have no conditions, they overlap
    if (a.conditionsDescription === '—' && b.conditionsDescription === '—') return true
    
    // If one has conditions and the other doesn't, they overlap
    if (a.conditionsDescription === '—' || b.conditionsDescription === '—') return true
    
    // For now, assume they don't overlap if both have different conditions
    return false
  }

  // Get unique values for filters
  const uniqueObjects = triggerGroups.map(g => ({
    name: g.objectName,
    count: g.flowCount
  }))

  // Get unique trigger types
  const uniqueTriggerTypes = [...new Set(
    triggerGroups.flatMap(g => g.triggers.map(t => {
      // Extract base trigger type (Before Save or After Save)
      const match = t.triggerType.match(/^(Before Save|After Save)/);
      return match ? match[1] : t.triggerType;
    }))
  )].sort()

  // Get unique trigger fields (filtered by selected object)
  const uniqueTriggerFields = [...new Set(
    triggerGroups
      .filter(g => selectedObject === 'all' || g.objectName === selectedObject)
      .flatMap(g =>
        g.triggers.flatMap(t => t.triggerFields.filter(f => f))
      )
  )].sort()

  // Filter trigger groups based on selected filters and search term
  const filteredGroups = triggerGroups
    .filter(group => selectedObject === 'all' || group.objectName === selectedObject)
    .map(group => ({
      ...group,
      triggers: group.triggers.filter(trigger => {
        // Type filter
        if (selectedTriggerType !== 'all' && !trigger.triggerType.includes(selectedTriggerType)) return false
        
        // Trigger field filter
        if (selectedTriggerField !== 'all' && !trigger.triggerFields.includes(selectedTriggerField)) return false
        
        // Search term filter
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase()
          const matchesSearch = 
            trigger.flowName.toLowerCase().includes(searchLower) ||
            trigger.matrixDisplay.toLowerCase().includes(searchLower) ||
            trigger.conditionsDescription.toLowerCase().includes(searchLower) ||
            trigger.triggerType.toLowerCase().includes(searchLower)
          if (!matchesSearch) return false
        }
        
        return true
      })
    }))
    .filter(group => group.triggers.length > 0)

  // Refresh trigger extraction function
  const refreshTriggerExtraction = async () => {
    if (!selectedOrgId) return
    
    setIsRefreshing(true)
    setError(null)
    
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        throw new Error('Not authenticated')
      }
      
      const response = await fetch('/api/triggers/refresh-extraction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ orgId: selectedOrgId })
      })
      
      if (!response.ok) {
        let errorMessage = 'Failed to refresh trigger extraction'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = `${response.status}: ${response.statusText}`
        }
        throw new Error(errorMessage)
      }
      
      const result = await response.json()
      
      // Show success message
      alert(`Trigger extraction refreshed successfully!\n\nProcessed: ${result.processed} flows\nErrors: ${result.errors}\nTotal: ${result.total}`)
      
      // Reload the trigger data
      window.location.reload()
    } catch (err: any) {
      console.error('Error refreshing trigger extraction:', err)
      setError(err)
      alert(`Error refreshing trigger extraction: ${err.message}`)
    } finally {
      setIsRefreshing(false)
    }
  }

  // Convert trigger groups to field-centric format
  const convertToFieldCentricFormat = () => {
    const fieldCentricData: any = {}
    
    // We need to access the raw flow data to get triggerOperations
    // For now, let's parse from the triggerType string
    filteredGroups.forEach(group => {
      const flows = group.triggers.map(trigger => {
        // Parse trigger type to extract operation
        // Format is like "Before Save (Create/Update)" or "After Save (Create)"
        const triggerTypeMatch = trigger.triggerType.match(/\((.*?)\)/)
        const operationsStr = triggerTypeMatch ? triggerTypeMatch[1] : ''
        
        // Determine operation type for the component
        let operation: 'create' | 'update' | 'create_update' = 'update'
        
        // Check for both lowercase and capitalized versions
        const hasCreate = operationsStr.toLowerCase().includes('create')
        const hasUpdate = operationsStr.toLowerCase().includes('update')
        
        if (hasCreate && hasUpdate) {
          operation = 'create_update'
        } else if (hasCreate) {
          operation = 'create'
        } else if (hasUpdate) {
          operation = 'update'
        }
        
        // Parse conditions from conditionsDescription
        const conditions: Record<string, string[]> = {}
        if (trigger.conditionsDescription && trigger.conditionsDescription !== '—') {
          // Simple parsing - you may need to enhance this based on actual format
          const conditionParts = trigger.conditionsDescription.split(', ')
          conditionParts.forEach(part => {
            const match = part.match(/^(.+?) (equals|does not equal|is null|is not null|contains|starts with|greater than|less than) (.+)$/)
            if (match) {
              const [, field, operator, value] = match
              if (!conditions[field]) conditions[field] = []
              conditions[field].push(`${operator} ${value}`)
            }
          })
        }

        // Filter trigger fields if a specific field is selected
        const filteredTriggerFields = selectedTriggerField === 'all'
          ? trigger.triggerFields
          : trigger.triggerFields.filter(f => f === selectedTriggerField)

        // Filter conditions to only include the selected field
        const filteredConditions: Record<string, string[]> = {}
        if (selectedTriggerField === 'all') {
          Object.assign(filteredConditions, conditions)
        } else if (conditions[selectedTriggerField]) {
          filteredConditions[selectedTriggerField] = conditions[selectedTriggerField]
        }

        return {
          operation,
          triggerFields: filteredTriggerFields,
          conditions: filteredConditions,
          flowName: trigger.flowName,
          flowId: trigger.flowId,
          performanceGrade: trigger.performanceGrade,
          performanceScore: trigger.performanceScore
        }
      })
      
      fieldCentricData[group.objectName] = flows
    })
    
    return fieldCentricData
  }

  // Export to CSV function
  const exportToCSV = () => {
    // Prepare CSV headers
    const headers = ['Object', 'Flow Name', 'Trigger Type', 'Trigger Fields', 'Additional Conditions', 'Performance Grade', 'Performance Score']
    
    // Prepare CSV rows
    const rows: string[][] = []
    
    filteredGroups.forEach(group => {
      group.triggers.forEach(trigger => {
        rows.push([
          group.objectName,
          trigger.flowName,
          trigger.triggerType,
          trigger.matrixDisplay,
          trigger.conditionsDescription,
          trigger.performanceGrade || '—',
          trigger.performanceScore?.toString() || '—'
        ])
      })
    })
    
    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...rows.map(row => 
        row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma, newline, or quotes
          const escaped = cell.replace(/"/g, '""')
          return /[,\n"]/.test(cell) ? `"${escaped}"` : escaped
        }).join(',')
      )
    ].join('\n')
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    
    link.setAttribute('href', url)
    link.setAttribute('download', `flow-trigger-matrix-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onSignOut={signOut} />

      <div className="py-10">
        <header>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <h1 className="text-3xl font-bold leading-tight text-gray-900">
                Entry Criteria
              </h1>
              <div className="flex space-x-2">
                <OrgSwitcher currentOrgId={selectedOrgId} />
                {/* Refresh Triggers button - only show for non-Viewer roles when role is loaded */}
                {userRole && userRole !== 'Viewer' && (
                  <button
                    onClick={refreshTriggerExtraction}
                    disabled={isRefreshing || !selectedOrgId}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRefreshing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh Triggers
                      </>
                    )}
                  </button>
                )}
                {/* Export as CSV button - only show for non-Viewer roles when role is loaded */}
                {userRole && userRole !== 'Viewer' && (
                  <button
                    onClick={exportToCSV}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export as CSV
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {/* Object Filter */}
              <div>
                <label htmlFor="object-filter" className="block text-sm font-medium text-gray-700">
                  Object
                </label>
                <select
                  id="object-filter"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base text-gray-900 bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  value={selectedObject}
                  onChange={(e) => setSelectedObject(e.target.value)}
                >
                  <option value="all">All Objects</option>
                  {uniqueObjects.map((obj) => (
                    <option key={obj.name} value={obj.name}>
                      {cleanObjectName(obj.name)} ({obj.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Type Filter */}
              <div>
                <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700">
                  Type
                </label>
                <select
                  id="type-filter"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base text-gray-900 bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  value={selectedTriggerType}
                  onChange={(e) => setSelectedTriggerType(e.target.value)}
                >
                  <option value="all">All Types</option>
                  {uniqueTriggerTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Trigger Field Filter */}
              <div>
                <label htmlFor="field-filter" className="block text-sm font-medium text-gray-700">
                  Trigger Field
                </label>
                <select
                  id="field-filter"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base text-gray-900 bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  value={selectedTriggerField}
                  onChange={(e) => setSelectedTriggerField(e.target.value)}
                  disabled={uniqueTriggerFields.length === 0}
                >
                  <option value="all">All Fields</option>
                  {uniqueTriggerFields.map((field) => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search Box */}
              <div className="sm:col-span-2">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                  Search
                </label>
                <input
                  type="text"
                  name="search"
                  id="search"
                  className="mt-1 block w-full px-3 py-2 text-gray-900 border border-gray-300 bg-white rounded-md shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search flows, fields, conditions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </header>

        <main>
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="px-4 py-8 sm:px-0">
              {/* Loading state */}
              {isLoading && (
                <div className="text-center py-8">
                  <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm text-gray-700">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading entry criteria...
                  </div>
                </div>
              )}

              {/* Error state */}
              {error && !isLoading && (
                <div className="text-center py-8">
                  <div className="text-red-600">
                    <p className="text-lg font-medium">Error loading data</p>
                    <p className="text-sm mt-2">{error.message}</p>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!isLoading && !error && filteredGroups.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-500">
                    <p className="text-lg font-medium">No entry criteria found</p>
                    <p className="text-sm mt-2">
                      {triggerGroups.length === 0 
                        ? "This organization doesn't have any record-triggered flows."
                        : "No flows match the selected filters. Try adjusting your filter criteria."}
                    </p>
                  </div>
                </div>
              )}

              {/* Field-Centric View */}
              {!isLoading && !error && (
                <FieldCentricTriggerTable 
                  triggerData={convertToFieldCentricFormat()} 
                  orgId={selectedOrgId || ''} 
                />
              )}
            </div>
          </div>
        </main>
      </div>
      
      {/* Chatbot */}
      <Chatbot />
    </div>
  )
}

// Get grade color helper
function getGradeColor(grade: string | null): string {
  switch (grade) {
    case 'A': return 'bg-green-100 text-green-800'
    case 'B': return 'bg-blue-100 text-blue-800'
    case 'C': return 'bg-yellow-100 text-yellow-800'
    case 'D': return 'bg-orange-100 text-orange-800'
    case 'F': return 'bg-red-100 text-red-800'
    case 'I': return 'bg-gray-100 text-gray-800'
    default: return 'bg-gray-100 text-gray-600'
  }
}